import { useState, useEffect, useRef } from 'react';

/**
 * @param {number} threshold - Минимальное расстояние (px)
 * @param {number} velocityThreshold - Минимальная скорость (px/ms)
 * @param {number} idleResetMs - через сколько ms без нормального движения сбрасывать базу
 */
export const useSwipe = (threshold = 50, velocityThreshold = 0.5, idleResetMs = 120) => {
    const [isSwipingUp, setIsSwipingUp] = useState(null);

    const ref = useRef({
        hasBase: false,
        baseY: 0,
        baseTime: 0,
        lastTime: 0,
        processed: false,
        touchId: null,
    });

    useEffect(() => {
        const handleTouchMove = (e) => {
            const t = e.touches[0];
            if (!t) return;

            const now = Date.now();
            const y = t.clientY;

            // Если палец "поменялся" (другая touch-точка), начинаем заново
            if (ref.current.touchId !== null && ref.current.touchId !== t.identifier) {
                ref.current = { ...ref.current, hasBase: false, processed: false, touchId: t.identifier };
            } else if (ref.current.touchId === null) {
                ref.current.touchId = t.identifier;
            }

            // Если долго не было движения — сбрасываем базу (чтобы не копить diff бесконечно)
            if (ref.current.lastTime && now - ref.current.lastTime > idleResetMs) {
                ref.current.hasBase = false;
                ref.current.processed = false;
            }
            ref.current.lastTime = now;

            // База (аналог touchstart) — первый touchmove после reset
            if (!ref.current.hasBase) {
                ref.current.hasBase = true;
                ref.current.baseY = y;
                ref.current.baseTime = now;
                ref.current.processed = false;
                return;
            }

            if (ref.current.processed) return;

            const diffY = ref.current.baseY - y; // >0 вверх
            const dt = now - ref.current.baseTime;
            if (dt <= 0) return;

            const velocity = Math.abs(diffY) / dt;

            if (Math.abs(diffY) >= threshold && velocity >= velocityThreshold) {
                const up = diffY > 0;
                setIsSwipingUp(up);

                // Важно: чтобы работало "без отрыва пальца"
                // делаем reset базы, и следующий свайп можно поймать дальше по движению
                ref.current.hasBase = false;
                ref.current.processed = true; // защита от двойного срабатывания в рамках того же микродвижения
            }
        };

        window.addEventListener('touchmove', handleTouchMove, { passive: true });

        return () => window.removeEventListener('touchmove', handleTouchMove);
    }, [threshold, velocityThreshold, idleResetMs]);

    return isSwipingUp;
};